import {
  DropdownMenu,
  type DropdownMenuContentProps,
  type DropdownMenuPortalProps,
  type DropdownMenuRootOptions,
} from "@kobalte/core/dropdown-menu";
import { createPresence } from "@solid-primitives/presence";
import { type JSX, type Signal, batch, createSignal, splitProps } from "solid-js";
import { twMerge } from "tailwind-merge";
import { AnimateChildren } from "../animation/AnimateChildren";

export const StDropdown = (
  props: {
    children: JSX.Element;
    content: JSX.Element;
    portalProps?: Omit<DropdownMenuPortalProps, "children">;
    contentProps?: DropdownMenuContentProps & JSX.HTMLAttributes<HTMLDivElement>;
  } & DropdownMenuRootOptions,
) => {
  const signal = props.onOpenChange ? ([() => props.open, props.onOpenChange] as Signal<boolean>) : createSignal(false);
  const [open, setOpen] = signal;

  const duration = 100;
  const presence = createPresence(() => props.open || open(), {
    transitionDuration: duration,
  });

  const [split, rest] = splitProps(props, ["content", "portalProps", "contentProps"]);
  return (
    <DropdownMenu {...rest} onOpenChange={setOpen} open={open()} forceMount={presence.isMounted()}>
      {rest.children}

      <DropdownMenu.Portal {...split.portalProps}>
        <AnimateChildren
          show={open()}
          duration={duration}
          presence={presence}
          enter={{
            scale: [0.9, 1],
            opacity: [0, 1],
          }}
          exit={{
            scale: 0.9,
            opacity: 0,
          }}
        >
          {(anim) => (
            <DropdownMenu.Content
              {...split.contentProps}
              ref={anim.ref}
              class={twMerge(
                `text-sm px-2 py-1 origin-[var(--kb-menu-content-transform-origin)]
        rounded-lg shadow-xl bg-tile text-primary-text border border-primary-border
        `,
                split.contentProps?.class,
              )}
            >
              {split.content}
            </DropdownMenu.Content>
          )}
        </AnimateChildren>
      </DropdownMenu.Portal>
    </DropdownMenu>
  );
};

export const StDropdownTrigger = DropdownMenu.Trigger;
export const StDropdownItem = DropdownMenu.Item;
export const StDropdownContent = DropdownMenu.Content;
export const StDropdownSub = DropdownMenu.Sub;
export const StDropdownSubTrigger = DropdownMenu.SubTrigger;
export const StDropdownSubContent = DropdownMenu.SubContent;
export const StDropdownPortal = DropdownMenu.Portal;
